import { AlertCircleIcon, InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { Skeleton } from '@/components/ui/skeleton';

import { formatTotalBookedHours } from '../../../helpers/assignment/booked-total';

interface AssignmentTotalBookedFieldProps {
  bookedMinutes: number | undefined;
  isLoading: boolean;
  isError: boolean;
  doSkipWeekdays: boolean;
  isBackgroundWork: boolean;
}

const getTooltipKey = (doSkipWeekdays: boolean, isBackgroundWork: boolean) => {
  if (isBackgroundWork) {
    return doSkipWeekdays
      ? 'form.totalBooked.tooltip.weekdaysBackgroundWork'
      : 'form.totalBooked.tooltip.allDaysBackgroundWork';
  }
  return doSkipWeekdays ? 'form.totalBooked.tooltip.weekdays' : 'form.totalBooked.tooltip.allDays';
};

/** Read-only total beside "Hours per day", sized to its content so the hours input takes the rest */
export function AssignmentTotalBookedField({
  bookedMinutes,
  isLoading,
  isError,
  doSkipWeekdays,
  isBackgroundWork,
}: AssignmentTotalBookedFieldProps) {
  const { t } = useTranslation('authenticated.planner');

  const renderValue = () => {
    if (isLoading) {
      return (
        <Skeleton
          className="h-4 w-12 bg-background"
          data-testid="assignment-modal-total-booked-skeleton"
        />
      );
    }
    if (isError) {
      return (
        <SimpleTooltip
          content={t('api.error.bookedMinutesPreviewFailed')}
          contentClassName="max-w-56"
        >
          <span className="flex items-center gap-1 text-destructive">
            {t('form.totalBooked.empty')}
            <AlertCircleIcon size={14} className="shrink-0" />
          </span>
        </SimpleTooltip>
      );
    }
    if (bookedMinutes === undefined) return t('form.totalBooked.empty');

    return formatTotalBookedHours(bookedMinutes);
  };

  return (
    <div className="grid shrink-0 gap-2">
      <span className="flex items-center gap-1 text-sm font-medium leading-none">
        {t('form.totalBooked.label')}
        <SimpleTooltip
          content={t(getTooltipKey(doSkipWeekdays, isBackgroundWork))}
          contentClassName="max-w-56"
        >
          <InfoIcon size={14} className="shrink-0 text-muted-foreground" />
        </SimpleTooltip>
      </span>
      <div
        className="flex h-[40px] min-w-16 items-center whitespace-nowrap rounded-md border bg-muted px-3 text-sm"
        data-testid="assignment-modal-total-booked"
        aria-live="polite"
      >
        {renderValue()}
      </div>
    </div>
  );
}
